import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Plane, AlertTriangle, CheckCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";

// Conflict zone airspaces with NOTAM-based closures
const CONFLICT_AIRSPACES = [
  { code: "OIIX", name: "Iran (Tehran FIR)", status: "closed", reason: "Active conflict zone" },
  { code: "ORBB", name: "Iraq (Baghdad FIR)", status: "restricted", reason: "Military operations" },
  { code: "OLBB", name: "Lebanon (Beirut FIR)", status: "closed", reason: "Active conflict zone" },
  { code: "OSTT", name: "Syria (Damascus FIR)", status: "closed", reason: "Long-term conflict" },
  { code: "UKBV", name: "Ukraine (East)", status: "closed", reason: "Active conflict zone" },
  { code: "LLLL", name: "Israel (Tel Aviv FIR)", status: "restricted", reason: "Missile threat" },
  { code: "HSSS", name: "Sudan (Khartoum FIR)", status: "closed", reason: "Civil war" },
  { code: "OYSC", name: "Yemen (Sana'a FIR)", status: "closed", reason: "Houthi activity" },
];

const statusColors: Record<string, string> = {
  closed: "text-red-400 bg-red-500/10 border-red-500/30",
  restricted: "text-orange-400 bg-orange-500/10 border-orange-500/30",
  open: "text-green-400 bg-green-500/10 border-green-500/30",
};

const AirlineIntelligence = () => {
  const { data: eventsData } = useQuery({
    queryKey: ["events-airline"],
    queryFn: async () => {
      const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
      const { data, error } = await supabase
        .from("events")
        .select("country, event_type, title")
        .gte("occurred_at", since)
        .limit(200);
      if (error) throw error;
      return data || [];
    },
    staleTime: 120000,
  });

  const events = eventsData || [];
  const closedCount = CONFLICT_AIRSPACES.filter((a) => a.status === "closed").length;

  return (
    <section id="airline-intel" className="py-8">
      <div className="container mx-auto px-4">
        <div className="mb-6 flex items-center gap-3">
          <Plane className="h-5 w-5 text-primary" />
          <h2 className="font-mono text-lg font-bold uppercase tracking-wider text-foreground">
            Airline & Airspace Intelligence
          </h2>
          <Badge variant="outline" className="text-[9px] font-mono text-red-400 border-red-400/30 bg-red-500/10">
            {closedCount} airspaces closed
          </Badge>
        </div>

        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="grid grid-cols-[80px_1fr_100px_1fr] gap-2 px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground border-b border-border">
            <span>FIR</span>
            <span>Airspace</span>
            <span>Status</span>
            <span>Reason</span>
          </div>
          <div className="divide-y divide-border/50">
            {CONFLICT_AIRSPACES.map((a) => (
              <div key={a.code} className="grid grid-cols-[80px_1fr_100px_1fr] items-center gap-2 px-3 py-2.5 hover:bg-accent/50 transition-colors">
                <span className="text-xs font-mono font-bold text-foreground">{a.code}</span>
                <span className="text-xs text-foreground">{a.name}</span>
                <div>
                  <span className={`inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded border font-semibold ${statusColors[a.status]}`}>
                    {a.status === "closed" ? <AlertTriangle className="h-2.5 w-2.5" /> : a.status === "open" ? <CheckCircle className="h-2.5 w-2.5" /> : <AlertTriangle className="h-2.5 w-2.5" />}
                    {a.status.toUpperCase()}
                  </span>
                </div>
                <span className="text-[10px] text-muted-foreground">{a.reason}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="mt-2 text-[10px] font-mono text-muted-foreground text-right">
          Airspace data based on active NOTAMs and conflict zone monitoring
        </p>
      </div>
    </section>
  );
};

export default AirlineIntelligence;
